import { Injectable, effect, signal } from '@angular/core';
import * as Rx from 'rxjs';
import { Subscription } from 'rxjs';
import { ChatWithOpenaiResponse } from '../../../../../backend/src/core/openai/openai.gate';
import { UpdateCreditsPayload, WebSocketResponseType } from '../../../../../backend/src/core/websocket/websocket.gate';
import { environment } from '../../../environments/environment';
import { ChatMessage } from '../chat/chat';
import { LocationService } from './../core-item/location.service';
import { UserClerkService } from './../user/user-clerk.service';
import { WebSocketBackend } from './websocket';
import { WebSocketSendService } from './websocket-send.service';

export interface Message {
  action: string;
  content: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private readonly RECONNECT_INTERVAL = 1000;

  private webSocket?: WebSocket;

  private sendOpenaiResponseSubject = new Rx.Subject<ChatWithOpenaiResponse>();

  agentChat = signal<string>('');
  chatMessage = signal<ChatMessage | undefined>(undefined);

  agentMessage = signal<{ name: string; content: any; solutionId: string; sourceList?: any[] } | undefined>(undefined);

  streamingChat = signal<
    | {
        coreId: string;
        additionalId: string | number;
        content: string;
        finishedReason: string;
      }
    | undefined
  >(undefined);

  isConnected = signal<boolean>(false);

  openaiResponseChange$() {
    return this.sendOpenaiResponseSubject.asObservable();
  }

  constructor(
    private userClerkService: UserClerkService,
    private webSocketSendService: WebSocketSendService,
    private locationService: LocationService
  ) {
    effect(() => {
      const coreId = this.locationService.currentCoreId();
      this.webSocketSendService.setLocation({ currentCoreItemId: coreId });
    });
  }

  private _coreLocationSubscription?: Subscription;

  connect() {
    if (!this.isConnected()) {
      this.webSocket = new WebSocket(environment.lambdaWebSocketUrl);

      this.webSocket?.addEventListener('error', (event) => {
        console.log('WebSocket error: ', event);
      });

      const that = this;

      this.webSocketSendService.init(this.webSocket, this.isConnected);

      this.webSocket.onopen = function (this: WebSocket, ev: Event) {
        that.isConnected.set(true);
        console.info('WebSocket Connected');

        const coreId = that.locationService.currentCoreId();
        that.webSocketSendService.setLocation({ currentCoreItemId: coreId });
      };

      this.webSocket.onmessage = function (this: WebSocket, ev: MessageEvent) {
        if (ev?.data) {
          const message = JSON.parse(ev.data);
          if (message) {
            if (message.type === WebSocketResponseType.publicPersonChat) {
            } else if (message.type === WebSocketResponseType.agentChat) {
              that.agentMessage.set(message.payload);
            } else if (message.type === WebSocketResponseType.onlineUserLocationList) {
              that.onlineUserLocationListEvent(message.payload);
            } else if (message.type === WebSocketResponseType.chatMessage) {
              that.chatMessage.set(message.payload);
            } else if (message.type === WebSocketResponseType.streamingChat) {
              that.streamingChat.set(message.payload);
            } else if (message.type === WebSocketResponseType.updateCredits) {
              const payload = message.payload as UpdateCreditsPayload;
              if (payload.userId === that.userClerkService.userId) {
                that.userClerkService
                  .reloadUser$()
                  .pipe(Rx.take(1))
                  .subscribe((_) => {});
              }
            }
          }
        }
      };

      this.webSocket.onclose = function (this: WebSocket, ev: CloseEvent) {
        that.isConnected.set(false);
        console.info('Socket is closed. Reconnect will be attempted in 1 second.', ev.reason);

        if (that._coreLocationSubscription) {
          that._coreLocationSubscription.unsubscribe();
        }
        that.webSocket = undefined;
        setTimeout(function () {
          that.connect();
        }, that.RECONNECT_INTERVAL);
      };

      this.webSocket.onerror = function (this: WebSocket, ev: Event) {
        console.error('Socket encountered error: ', ev, 'Closing socket');
        this.close();
      };
    }
  }

  // TODO: Finish
  private onlineUserLocationListEvent(websocketBackendList: WebSocketBackend[]) {
    const onlineUserBoardIdSet = new Set<string>(websocketBackendList?.map((c) => c.personalBoardId) ?? []);
    // const allCachedBoardList = this.boardService.getAllBoardList();

    // for (const board of allCachedBoardList) {
    //   if (onlineUserBoardIdSet.has(board.id)) {
    //     board.isOnline = true;
    //   } else {
    //     board.isOnline = false;
    //   }
    // }

    // this._onlineUserLocationListSubject$.next(websocketBackendList);
  }

  close() {
    // TODO: Update block item changes.
    this.webSocket?.close();
  }
}
